import React, { useState, useMemo } from 'react';
import { Link, Banner, Button } from '@tackle-io/platform-ui';
import { ampli } from 'utils/analytics/ampli';
import { INTEGRATIONS } from '../../../constants';
import { AwsCredentialsState } from '../../../api/awsCredentialsStatus';
import {
  Grid,
  Typography,
  Box,
  IconButton,
  makeStyles,
  Divider,
} from 'vendor/material';
import ErrorIcon from '../assets/ErrorIcon';
import { ChevronRight, ChevronDown } from 'mdi-material-ui';
import GeneralListing from '../assets/GeneralListing';
import GeneralGrowth from '../assets/GeneralGrowth';
import ProductTackleOffers from '../assets/ProductTackleOffers';
import ProductCoSell from '../assets/ProductCoSell';
import PermissionTimeSubTitle from '../../../../../components/PermissionTimeSubTitle/PermissionTimeSubTitle';

const useStyles = makeStyles((theme) => ({
  headerContainer: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    '&:hover': {
      opacity: 0.8,
    },
  },
  disabledHeaderContainer: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'default',
    '&:hover': {
      opacity: 1,
    },
  },
  disabledCard: {
    background: '#EBECF0',
  },
  disabledText: {
    color: '#5E6C84',
  },
  headerAwsConnectTitle: {
    color: theme.palette.BLUE400,
    marginLeft: theme.spacing(2),
    marginTop: theme.spacing(3),
    fontWeight: 600,
    fontSize: theme.typography.pxToRem(20),
  },
  chevron: {
    transform: 'rotate(0deg)',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
    '&.expanded': {
      transform: 'rotate(180deg)',
    },
  },
  card: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(2),
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(4),
  },
  getConnected: {
    color: theme.palette.NEUTRAL300,
    marginLeft: theme.spacing(2),
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(1),
    fontSize: theme.typography.pxToRem(12),
    fontWeight: 600,
  },
  successfullConnectionText: {
    color: theme.palette.TEAL900,
    fontSize: theme.typography.pxToRem(16),
    marginLeft: theme.spacing(2),
    fontWeight: 700,
  },
  emailIcon: {
    color: theme.palette.NEUTRAL700,
    display: 'flex',
    alignItems: 'center',
    marginRight: '8px',
  },
  smallCopyBold: {
    color: theme.palette.NEUTRAL700,
    fontWeight: 700,
    fontSize: theme.typography.pxToRem(12),
  },
  smallCopy: {
    color: theme.palette.NEUTRAL300,
    fontWeight: 400,
    fontSize: theme.typography.pxToRem(12),
  },
  smallLabel: {
    color: '#0052CC',
    fontWeight: 600,
    fontSize: theme.typography.pxToRem(12),
  },
  standardCopyBold: {
    color: '#0052CC',
    fontWeight: 600,
    fontSize: theme.typography.pxToRem(16),
  },
  standardCopy: {
    color: theme.palette.TEAL900,
    fontWeight: 400,
    fontSize: theme.typography.pxToRem(16),
  },
  stepText: {
    color: theme.palette.NEUTRAL700,
    fontWeight: 400,
    fontSize: theme.typography.pxToRem(12),
    marginLeft: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  stepNumber: {
    marginRight: theme.spacing(1),
  },
  marginLeft20: {
    marginLeft: theme.spacing(2),
  },
}));

interface Props {
  generateUrl: () => string;
  hasRequiredData: boolean;
  onConnect?: () => void;
  isStepComplete?: boolean;
  lastAssumeRoleStatus: AwsCredentialsState['lastAssumeRoleStatus'];
  disableConnect?: boolean;
  isBothStepsComplete?: boolean;
}

const ConnectToAWSCard: React.FC<Props> = ({
  generateUrl,
  hasRequiredData,
  onConnect,
  isStepComplete,
  lastAssumeRoleStatus,
  disableConnect,
  isBothStepsComplete,
}) => {
  // All hooks at the start
  const classes = useStyles();
  const [isStep1Expanded, setIsStep1Expanded] = useState(true);
  const [showMarketplaceSteps, setShowMarketplaceSteps] = useState(false);
  const [showCosellSteps, setShowCosellSteps] = useState(false);

  React.useEffect(() => {
    // Close if complete
    if (isStepComplete) {
      setIsStep1Expanded(false);
      return;
    }

    // Keep expanded if error
    if (lastAssumeRoleStatus === 'failed') {
      setIsStep1Expanded(true);
      return;
    }

    // Keep expanded if first render or null status
    if (lastAssumeRoleStatus === null) {
      setIsStep1Expanded(true);
      return;
    }
  }, [lastAssumeRoleStatus, isStepComplete]);

  // Determine header color and error icon visibility based on connection status
  const { headerColor, showErrorIcon } = useMemo(() => {
    // Error state
    if (lastAssumeRoleStatus === 'failed') {
      return { headerColor: '#DE350B', showErrorIcon: true };
    }

    // Success state
    if (lastAssumeRoleStatus === 'successful' && isStepComplete) {
      return { headerColor: '#00875A', showErrorIcon: false };
    }

    // Default state - blue when collapsed, dark when expanded
    return {
      headerColor: isStep1Expanded ? '#253858' : '#0052CC',
      showErrorIcon: false,
    };
  }, [lastAssumeRoleStatus, isStepComplete, isStep1Expanded]);

  return (
    <Box className={isBothStepsComplete ? classes.disabledCard : ''}>
      <Box
        className={
          isBothStepsComplete
            ? classes.disabledHeaderContainer
            : classes.headerContainer
        }
        onClick={
          isBothStepsComplete
            ? undefined
            : () => setIsStep1Expanded(!isStep1Expanded)
        }
      >
        <Box flex={1} mb={2}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography
              className={classes.headerAwsConnectTitle}
              style={{ color: isBothStepsComplete ? '#5E6C84' : headerColor }}
            >
              {showErrorIcon && <ErrorIcon />} Step 1: Connect to AWS
            </Typography>
            {!isBothStepsComplete && (
              <IconButton size="small">
                <ChevronDown
                  className={`${classes.chevron} ${
                    isStep1Expanded ? 'expanded' : ''
                  }`}
                />
              </IconButton>
            )}
          </Box>
          <Box
            ml={2}
            mt={1}
            className={isBothStepsComplete ? classes.disabledText : ''}
          >
            <PermissionTimeSubTitle
              permissions="AWS Admin"
              timeToComplete="5 minutes"
            />
          </Box>
        </Box>
      </Box>

      {isStep1Expanded && (
        <>
          <Box ml={2} mr={4} mb={6} mt={2}>
            <Grid item xs={12}>
              <Banner
                title="You must be signed into your AWS Marketplace account as the administrator."
                borderPosition="top"
              />
            </Grid>
          </Box>
          <Typography className={classes.getConnected}>
            Get connected to be able to:
          </Typography>
          <Grid container direction="row" alignItems="center">
            <Grid item>
              <Box display="flex" alignItems="center" mr={5} ml={2}>
                <GeneralListing />
                <Box ml={1}>
                  <Typography className={classes.smallCopyBold}>
                    List products
                  </Typography>
                  <Typography className={classes.smallCopy}>
                    Get listed on AWS Marketplace
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item>
              <Box display="flex" alignItems="center" mr={5}>
                <ProductCoSell />
                <Box ml={1}>
                  <Typography className={classes.smallCopyBold}>
                    Co-sell with ACE
                  </Typography>
                  <Typography className={classes.smallCopy}>
                    Register opportunities with ACE
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item>
              <Box display="flex" alignItems="center" mr={5}>
                <ProductTackleOffers />
                <Box ml={1}>
                  <Typography className={classes.smallCopyBold}>
                    Transact
                  </Typography>
                  <Typography className={classes.smallCopy}>
                    Create private offers
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item>
              <Box display="flex" alignItems="center">
                <GeneralGrowth />
                <Box ml={1}>
                  <Typography className={classes.smallCopyBold}>
                    Track performance
                  </Typography>
                  <Typography className={classes.smallCopy}>
                    Monitor critical metrics
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
          <Grid container direction="column" spacing={2}>
            <Box mt={6} ml={1}>
              <Grid item xs={10}>
                <Typography className={classes.successfullConnectionText}>
                  <b>
                    To ensure a successful connection, complete the following
                    steps before you connect.
                  </b>
                </Typography>
              </Grid>
            </Box>
            <Box ml={1} mb={2}>
              <Grid item className={classes.marginLeft20}>
                <Typography
                  className={classes.standardCopyBold}
                  style={{ color: '#253858' }}
                >
                  Setup your seller account on AWS Marketplace.
                </Typography>
              </Grid>
              <Grid item xs={10}>
                <Button
                  variant="text"
                  color="primary"
                  onClick={() => setShowMarketplaceSteps(!showMarketplaceSteps)}
                  endIcon={
                    showMarketplaceSteps ? (
                      <ChevronDown className={classes.smallLabel} />
                    ) : (
                      <ChevronRight className={classes.smallLabel} />
                    )
                  }
                >
                  <span className={classes.smallLabel}>
                    {showMarketplaceSteps ? 'Hide Steps' : 'See Steps'}
                  </span>
                </Button>
                {showMarketplaceSteps && (
                  <Box ml={2}>
                    <Typography className={classes.stepText}>
                      <span className={classes.stepNumber}>1.</span>
                      Identify which account
                      <Box mt={1} />
                      <Typography className={classes.stepText}>
                        This account will be used when publishing your listings
                        and managing disbursement of sales. We recommend
                        creating a brand new AWS account that is linked to your
                        master account. By creating a new account you are
                        isolating your production and development accounts from
                        your Tackle managed listings. You can create and link
                        accounts in{' '}
                        <Link
                          showExternalIcon
                          to="https://console.aws.amazon.com/organizations/home?#/accounts"
                          external
                        >
                          AWS Organizations.
                        </Link>
                        <Box mt={1} />
                        Once created, login to this account and complete the
                        next steps.
                      </Typography>
                    </Typography>
                    <Typography className={classes.stepText}>
                      <span className={classes.stepNumber}>2.</span>
                      Ensure that the account has access to create roles and
                      stacks, and configure financial services.
                    </Typography>
                    <Typography className={classes.stepText}>
                      <span className={classes.stepNumber}>3.</span>
                      Accept the{' '}
                      <Link
                        showExternalIcon
                        to="https://aws.amazon.com/marketplace/management/seller-settings/register/"
                        external
                      >
                        AWS Terms and Services
                      </Link>
                    </Typography>
                    <Typography className={classes.stepText}>
                      <span className={classes.stepNumber}>4.</span>
                      Complete the{' '}
                      <Link
                        showExternalIcon
                        to="https://aws.amazon.com/marketplace/management/seller-settings/income-tax/us/status"
                        external
                      >
                        Tax Information Interview
                      </Link>
                    </Typography>
                    <Typography className={classes.stepText}>
                      <span className={classes.stepNumber}>5.</span>
                      Complete the{' '}
                      <Link
                        showExternalIcon
                        to="https://aws.amazon.com/marketplace/management/seller-settings/account/bank"
                        external
                      >
                        Banking Information
                      </Link>
                    </Typography>
                    <Typography className={classes.stepText}>
                      <span className={classes.stepNumber}>6.</span>
                      Complete the{' '}
                      <Link
                        showExternalIcon
                        to="https://aws.amazon.com/marketplace/management/seller-settings/account"
                        external
                      >
                        Company Public Profile
                      </Link>
                    </Typography>
                    <Typography className={classes.stepText}>
                      <span className={classes.stepNumber}>7.</span>
                      Complete the{' '}
                      <Link
                        to="https://aws.amazon.com/marketplace/partners/management-tour"
                        external
                      >
                        Seller Registration
                      </Link>
                      . This is required to create marketplace listings.
                    </Typography>
                  </Box>
                )}
              </Grid>
            </Box>
            <Box my={2} mx={3}>
              <Divider />
            </Box>
            <Box mt={2} ml={1}>
              <Grid item xs={12} className={classes.marginLeft20}>
                <Typography
                  className={classes.standardCopyBold}
                  style={{ color: '#253858' }}
                >
                  Link your AWS Partner Central account to AWS Marketplace.
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="text"
                  color="primary"
                  onClick={() => setShowCosellSteps(!showCosellSteps)}
                  endIcon={
                    showCosellSteps ? (
                      <ChevronDown className={classes.smallLabel} />
                    ) : (
                      <ChevronRight className={classes.smallLabel} />
                    )
                  }
                >
                  <span className={classes.smallLabel}>
                    {showCosellSteps ? 'Hide Steps' : 'See Steps'}
                  </span>
                </Button>
                {showCosellSteps && (
                  <Box ml={2}>
                    <Typography className={classes.stepText}>
                      <span className={classes.stepNumber}>1.</span>
                      Sign in to{' '}
                      <Link
                        showExternalIcon
                        to="https://partnercentral.awspartner.com/partnercentral2/s/acecrmintegration"
                        external
                      >
                        Partner Central
                      </Link>
                      , with an Alliance Lead or Cloud Admin role.
                    </Typography>
                    <Typography className={classes.stepText}>
                      <span className={classes.stepNumber}>2.</span>
                      In the <b>Account Linking section </b>on the homepage,
                      click Link Account.
                    </Typography>
                    <Typography className={classes.stepText}>
                      <span className={classes.stepNumber}>3.</span>
                      On the <b>Account Linking page,</b> choose{' '}
                      <b>Link Account.</b> No, this isn't a duplicate of step 2.
                    </Typography>
                    <Typography className={classes.stepText}>
                      <span className={classes.stepNumber}>4.</span>
                      Select<b> IAM user,</b> and then enter the AWS Account ID
                      for your AWS account.
                    </Typography>
                    <Typography className={classes.stepText}>
                      <span className={classes.stepNumber}>5.</span>
                      Click <b>Next</b>, and then sign in to the AWS account.
                    </Typography>
                    <Typography className={classes.stepText}>
                      <span className={classes.stepNumber}>6.</span>
                      Click<b> Allow </b>to authorize the connection between
                      your AWS Partner Central and AWS accounts.
                    </Typography>
                  </Box>
                )}
              </Grid>
            </Box>
            <Grid item xs={12}>
              <Box
                ml={1}
                mb={2}
                mr={2}
                display="flex"
                justifyContent="flex-end"
              >
                {!hasRequiredData || disableConnect ? (
                  <Button
                    appearance="primary"
                    disabled={true}
                    data-testid="connect-to-aws-button"
                  >
                    Connect
                  </Button>
                ) : (
                  <Link to={generateUrl()} external>
                    <Button
                      appearance="primary"
                      data-testid="connect-to-aws-button"
                      onClick={async (e) => {
                        e.preventDefault();
                        ampli.buttonClicked({
                          button_location: 'integrations-connect-to-aws',
                          button_name: 'connect-to-aws-button',
                          button_product_area: INTEGRATIONS,
                        });
                        window.open(generateUrl(), '_blank');
                        onConnect?.();
                      }}
                    >
                      Connect
                    </Button>
                  </Link>
                )}
              </Box>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
};

export default ConnectToAWSCard;
